import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
// import {NotificationService} from './notification.service';
import { Subscription } from 'rxjs';
import { EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { HeaderDivisor } from '@/shared/components/layout/header-divisor/header-divisor';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { PrimeTemplate } from 'primeng/api';
import { SelectButton } from 'primeng/selectbutton';
import { TabContainerOptionType } from '@/shared/components/layout/tab-container/tab-container';
import { FormsModule } from '@angular/forms';
import { Avatar } from 'primeng/avatar';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { Card } from 'primeng/card';

interface TicketData {
    assignee?: string;
    startDate?: string;
    prLink?: string;
    reviewer?: string;
    releaseNotes?: string;
    rollbackReason?: string;
}

interface Ticket {
    id: string;
    title: string;
    description: string;
    status: 'todo' | 'doing' | 'review' | 'done';
    data: TicketData;
}

@Component({
    selector: 'app-root',
    imports: [CdkDropListGroup, CdkDropList, CdkDrag, CommonModule, RouterLink, HeaderDivisor, Button, Tooltip, PrimeTemplate, SelectButton, FormsModule, Avatar, CardContainer, Card],
    templateUrl: './dashboardOrientacoesTecnicas.html',
    styleUrls: ['./dashboardOrientacoesTecnicas.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardOrientacoesTecnicas implements OnInit, OnDestroy {
    // notificationService = inject(NotificationService);
    private sub: Subscription = new Subscription();

    ngOnInit() {
        // Aqui você define a "sua própria função" que será executada
        // this.sub = this.notificationService.notificacao$.subscribe((mensagem) => {
        //   this.minhaFuncaoCustomizada(mensagem);
        // });
    }

    minhaFuncaoCustomizada(texto: string) {
        this.saveTicket('Card Adicionado em ' + new Date().toLocaleString(), texto);
        // Lógica para exibir alerta, snackbar, etc.
    }

    ngOnDestroy() {
        // Importante cancelar a inscrição para evitar vazamento de memória
        this.sub.unsubscribe();
    }

    // Estado Principal
    tickets = signal<Ticket[]>([
        { id: '101', title: 'Setup Angular CDK', description: 'Configurar pacotes de drag drop.', status: 'todo', data: {} },
        { id: '102', title: 'Implementar Signals', description: 'Migrar estados para signals reativos.', status: 'doing', data: { assignee: 'Dev Angular' } }
    ]);

    // UI State
    pendingMove = signal<{ ticket: Ticket; from: string; to: string } | null>(null);
    showFormModal = signal(false);
    activeTicket = signal<Ticket | null>(null);
    isEditing = signal(false);
    movingTicketId = signal<string | null>(null);

    columns = [
        { id: 'todo', title: 'A Fazer', color: 'bg-slate-100/40 dark:bg-slate-800/40 backdrop-blur-md' },
        { id: 'doing', title: 'Fazendo', color: 'bg-blue-50/40 dark:bg-blue-900/20 backdrop-blur-md' },
        { id: 'review', title: 'Revisão', color: 'bg-purple-50/40 dark:bg-purple-900/20 backdrop-blur-md' },
        { id: 'done', title: 'Concluído', color: 'bg-green-50/40 dark:bg-green-900/20 backdrop-blur-md' }
    ];

    getTicketsByStatus(status: string) {
        return this.tickets().filter((t) => t.status === status);
    }

    getColumnTitle(id: string | undefined) {
        return this.columns.find((c) => c.id === id)?.title || id;
    }

    // Inicia a movimentação (manual ou via drag)
    initiateMove(ticket: Ticket, from: string, to: string) {
        this.movingTicketId.set(null);
        this.pendingMove.set({
            ticket: ticket,
            from: from,
            to: to as any
        });
    }

    // Intercepta o drop do CDK
    onDrop(event: CdkDragDrop<string>) {
        if (event.previousContainer === event.container) {
            // Reordenação na mesma coluna (não implementado modal para isso, apenas move)
            const colId = event.container.id as any;
            const colTickets = this.getTicketsByStatus(colId);
            moveItemInArray(colTickets, event.previousIndex, event.currentIndex);
            // Aqui teríamos que atualizar a ordem global se necessário
        } else {
            // Mudança de coluna -> Abre Modal
            const ticketId = event.item.element.nativeElement.querySelector('.font-mono')?.textContent?.replace('#', '') || '';
            const ticket = this.tickets().find((t) => t.id === ticketId);
            if (ticket) {
                this.initiateMove(ticket, event.previousContainer.id, event.container.id);
            }
        }
    }

    confirmMove(extraData: TicketData) {
        const pending = this.pendingMove();
        if (!pending) return;

        this.tickets.update((all) =>
            all.map((t) => {
                if (t.id === pending.ticket.id) {
                    return {
                        ...t,
                        status: pending.to as any,
                        data: { ...t.data, ...extraData }
                    };
                }
                return t;
            })
        );

        this.pendingMove.set(null);
    }

    cancelMove() {
        this.pendingMove.set(null);
    }

    toggleMoveMenu(ticketId: string, event: Event) {
        event.stopPropagation();
        if (this.movingTicketId() === ticketId) {
            this.movingTicketId.set(null);
        } else {
            this.movingTicketId.set(ticketId);
        }
    }

    // Novo / Editar
    openNewTicketModal(status: any = 'todo') {
        this.isEditing.set(false);
        this.activeTicket.set({ id: '', title: '', description: '', status, data: {} });
        this.showFormModal.set(true);
    }

    openEditModal(ticket: Ticket) {
        this.isEditing.set(true);
        this.activeTicket.set(ticket);
        this.showFormModal.set(true);
    }

    saveTicket(title: string, description: string) {
        if (!title) return;

        if (this.isEditing()) {
            this.tickets.update((all) => all.map((t) => (t.id === this.activeTicket()?.id ? { ...t, title, description } : t)));
        } else {
            const newTicket: Ticket = {
                id: Math.floor(Math.random() * 900 + 100).toString(),
                title,
                description,
                status: this.activeTicket()?.status || 'todo',
                data: {}
            };
            this.tickets.update((all) => [...all, newTicket]);
        }
        this.showFormModal.set(false);
    }

    protected readonly EInconPos = EInconPos;

    options: TabContainerOptionType[] = [
        { label: 'Demandas', value: 'demandas' },
        { label: 'Canceladas', value: 'canceladas' },
        { label: 'Finalizadas', value: 'finalizadas' }
    ];
    selectedValue: string = 'demandas';
    selectedTab = 'demandas';

    onChange(selectedTab: string): void {
        // Ao trocar de aba, sair do modo de edição
        this.selectedTab = selectedTab;
        this.selectedValue = selectedTab;
        this.atualizar();
    }

    atualizar() {}
}
